import React, { useEffect, useState, useCallback, useMemo, Suspense } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, startAfter } from "firebase/firestore"; 
import { db } from "../firebase";
import Spinner from "../components/Spinner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useNavigate } from 'react-router-dom';
import { 
  FaBriefcase, 
  FaUsers, 
  FaSearch, 
  FaUserTie, 
  FaMapMarkerAlt,
  FaFilter,
  FaPlus,
  FaChevronLeft,
  FaExclamationTriangle
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from '../components/ErrorBoundary';

import JobPostForm from "../components/JobPostForm";

// Premium Card Component
const CandidateCard = ({ candidate }) => (
  <motion.div 
    className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
    whileHover={{ scale: 1.02 }}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
  >
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-4">
        <div className="h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
          {candidate.displayName?.charAt(0) || 'U'}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{candidate.displayName || 'Anonymous'}</h3>
          <p className="text-indigo-600 flex items-center">
            <FaUserTie className="mr-2" />
            {candidate.role || 'Professional'}
          </p>
        </div>
      </div>
      
      {candidate.skills?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Skills</h4>
          <div className="flex flex-wrap gap-2">
            {candidate.skills.slice(0, 5).map((skill, i) => (
              <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                {skill}
              </span>
            ))}
            {candidate.skills.length > 5 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                +{candidate.skills.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center text-sm text-gray-500">
          <FaMapMarkerAlt className="mr-1" />
          {candidate.location || 'Location not specified'}
        </div>
        <button 
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
          onClick={() => toast.info('View profile functionality coming soon!')}
        >
          View Profile
        </button>
      </div>
    </div>
  </motion.div>
);

// Skill Filter Component
const SkillFilter = ({ skills, selectedSkills, onSkillToggle }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-medium text-gray-700">Filter by Skills</h3>
    <div className="space-y-2 max-h-40 overflow-y-auto p-1">
      {skills.map((skill) => (
        <label key={skill} className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedSkills.includes(skill)}
            onChange={() => onSkillToggle(skill)}
            className="rounded text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">{skill}</span>
        </label>
      ))}
    </div>
  </div>
);

// Constants
const CANDIDATES_PER_PAGE = 10;

// Lazy load heavy components
const ManageMyJobs = React.lazy(() => import('../components/ManageMyJobs'));
const JobApplications = React.lazy(() => import('../components/JobApplications'));

function OrganizationDashboard() {
  // Navigation
  const navigate = useNavigate();
  
  // State management - all state hooks at the top
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [orgProfile, setOrgProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('post'); // Default to 'post' tab
  const [selectedJob, setSelectedJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allSkills, setAllSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [refreshJobsKey, setRefreshJobsKey] = useState(0);
  const [error, setError] = useState(null);
  const [lastCandidate, setLastCandidate] = useState(null);
  const [hasMoreCandidates, setHasMoreCandidates] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Tab states
  const [loadedTabs, setLoadedTabs] = useState({
    post: true,  // Post tab is pre-loaded
    jobs: false,
    applications: false,
    candidates: false
  });
  
  const [loadingStates, setLoadingStates] = useState({
    post: false,
    jobs: false,
    applications: false,
    candidates: false
  });
  
  // Memoized values
  const tabs = useMemo(() => [
    { id: 'post', name: 'Post New Job', icon: <FaPlus className="mr-2" /> },
    { id: 'jobs', name: 'My Jobs', icon: <FaBriefcase className="mr-2" /> },
    { id: 'applications', name: 'Applications', icon: <FaUsers className="mr-2" /> },
    { id: 'candidates', name: 'Find Talent', icon: <FaUserTie className="mr-2" /> },
  ], []);
  
  // Memoized values that depend on state
  const currentUser = useMemo(() => getAuth().currentUser, []);
  
  // Handler functions - defined before effects that use them
  const handleTabChange = useCallback(async (tabId) => {
    // Don't do anything if we're already on this tab or loading
    if (tabId === activeTab || loadingStates[tabId]) return;
    
    // Set the active tab immediately for better UX
    setActiveTab(tabId);
    
    // If tab is already loaded, no need to fetch data
    if (loadedTabs[tabId]) return;
    
    try {
      // Set loading state for this tab
      setLoadingStates(prev => ({ ...prev, [tabId]: true }));
      
      // Load data based on tab
      if (tabId === 'jobs') {
        await fetchJobs();
      } else if (tabId === 'candidates') {
        await fetchCandidates();
      } else if (tabId === 'applications' && selectedJob) {
        await fetchApplications(selectedJob.id);
      }
      
      // Mark tab as loaded
      setLoadedTabs(prev => ({
        ...prev,
        [tabId]: true
      }));
      
    } catch (error) {
      console.error(`Error loading ${tabId} data:`, error);
      toast.error(`Failed to load ${tabId} data`);
    } finally {
      setLoadingStates(prev => ({ ...prev, [tabId]: false }));
    }
  }, [activeTab, loadingStates, loadedTabs, selectedJob]);
  
  // Handle job selection for applications tab
  const handleJobSelect = useCallback((job) => {
    setSelectedJob(job);
    if (activeTab !== 'applications') {
      handleTabChange('applications');
    } else {
      // Force reload applications if already on applications tab
      setLoadedTabs(prev => ({ ...prev, applications: false }));
      handleTabChange('applications');
    }
  }, [activeTab, handleTabChange]);
  
  // Fetch professionals (candidates) with pagination
  const fetchCandidates = useCallback(async (loadMore = false) => {
    console.log('fetchCandidates called with loadMore:', loadMore);
    
    // If we're not loading more and already have candidates, or if we're trying to load more but there are no more candidates
    if ((!loadMore && candidates.length > 0) || (loadMore && !hasMoreCandidates)) {
      console.log('Skipping fetch - already loaded or no more candidates');
      return;
    }
    
    try {
      const shouldLoadMore = loadMore && lastCandidate;
      const loadingState = shouldLoadMore ? setIsLoadingMore : setLoading;
      
      loadingState(true);
      console.log('Fetching candidates from Firestore...');
      
      const professionalsRef = collection(db, 'professionals');
      let q = query(
        professionalsRef, 
        orderBy('displayName', 'asc'),
        limit(CANDIDATES_PER_PAGE + 1) // +1 to check if there are more
      );
      
      if (shouldLoadMore) {
        console.log('Loading more candidates, starting after:', lastCandidate);
        q = query(q, startAfter(lastCandidate));
      }
      
      const querySnapshot = await getDocs(q);
      console.log('Query snapshot size:', querySnapshot.size);
      
      const candidatesData = [];
      const skillsSet = new Set([...allSkills]);
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Processing professional:', { id: doc.id, data });
        if (data.skills?.length) {
          data.skills.forEach(skill => skillsSet.add(skill));
        }
        candidatesData.push({ id: doc.id, ...data });
      });
      
      // Check if there are more candidates to load
      const hasMore = candidatesData.length > CANDIDATES_PER_PAGE;
      const newCandidates = hasMore ? candidatesData.slice(0, -1) : candidatesData;
      
      console.log('Processed candidates:', newCandidates.length, 'Has more:', hasMore);
      
      // Update state with the new candidates
      setCandidates(prev => {
        const updated = loadMore ? [...prev, ...newCandidates] : newCandidates;
        console.log('Setting candidates:', updated);
        return updated;
      });
      
      setFilteredCandidates(prev => {
        const updated = loadMore ? [...prev, ...newCandidates] : newCandidates;
        console.log('Setting filteredCandidates:', updated);
        return updated;
      });
      
      // Update skills list
      const uniqueSkills = Array.from(skillsSet).sort();
      console.log('Updating skills list:', uniqueSkills);
      setAllSkills(uniqueSkills);
      
      // Update pagination state
      if (querySnapshot.docs.length > 0) {
        console.log('Setting last candidate for pagination');
        setLastCandidate(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } else {
        console.log('No more candidates to load');
      }
      
      setHasMoreCandidates(hasMore);
      console.log('Fetch complete. Has more candidates:', hasMore);
      return candidatesData;
    } catch (err) {
      console.error('Error fetching candidates:', err);
      toast.error('Failed to load candidates');
      return [];
    } finally {
      if (loadMore) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [
    candidates.length, 
    hasMoreCandidates, 
    lastCandidate, 
    allSkills, 
    setCandidates, 
    setFilteredCandidates, 
    setAllSkills, 
    setLastCandidate, 
    setHasMoreCandidates,
    setIsLoadingMore,
    setLoading
  ]);
  
  // Filter candidates based on search term and selected skills
  useEffect(() => {
    if (activeTab === 'candidates') {
      let result = [...candidates];
      
      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        result = result.filter(candidate => 
          (candidate.displayName?.toLowerCase().includes(term) ||
          candidate.skills?.some(skill => skill.toLowerCase().includes(term)) ||
          candidate.role?.toLowerCase().includes(term))
        );
      }
      
      // Filter by selected skills
      if (selectedSkills.length > 0) {
        result = result.filter(candidate => 
          selectedSkills.every(skill => 
            candidate.skills?.some(s => s === skill)
          )
        );
      }
      
      setFilteredCandidates(result);
    }
  }, [searchTerm, selectedSkills, candidates, activeTab]);
  
  // Job selection is now handled by the handleJobSelect function above
  // This removes the duplicate declaration
  
  // Toggle skill filter
  const toggleSkill = useCallback((skill) => {
    setSelectedSkills(prev => 
      prev.includes(skill) 
        ? prev.filter(s => s !== skill) 
        : [...prev, skill]
    );
  }, []);
  
  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedSkills([]);
  }, []);
  
  // Close filters when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isFilterOpen && !event.target.closest('.filter-container')) {
        setIsFilterOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  // Fetch all jobs for this organization
  const fetchJobs = useCallback(async (userId = null) => {
    try {
      const auth = getAuth();
      const currentUser = userId ? { uid: userId } : auth.currentUser;
      
      if (!currentUser) {
        navigate('/login');
        return [];
      }
      
      // Fetch jobs for this organization
      const jobsRef = collection(db, 'cdcJobs');
      const q = query(jobsRef, where('orgId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const jobsData = [];
      querySnapshot.forEach((doc) => {
        jobsData.push({ id: doc.id, ...doc.data() });
      });
      
      setJobs(jobsData);
      return jobsData;
    } catch (err) {
      console.error('Error fetching jobs:', err);
      toast.error('Failed to load jobs');
      return [];
    }
  }, [navigate]);
  
  // Fetch jobs when component mounts or user changes
  useEffect(() => {
    if (user?.uid) {
      fetchJobs(user.uid);
    }
  }, [user?.uid, fetchJobs]);

  // Fetch applications for the selected job
  const fetchApplications = useCallback(async (jobId) => {
    if (!jobId) return;
    
    try {
      setLoading(true);
      const applicationsRef = collection(db, 'jobApplications');
      const q = query(applicationsRef, where('jobId', '==', jobId));
      const querySnapshot = await getDocs(q);
      
      const applicationsData = [];
      for (const doc of querySnapshot.docs) {
        const appData = doc.data();
        // Try to get user data from the applicantId field first, fall back to userId
        const userId = appData.applicantId || appData.userId;
        if (userId) {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            applicationsData.push({
              id: doc.id,
              ...appData,
              user: userDoc.data()
            });
          }
        }
      }
      
      setApplications(applicationsData);
    } catch (err) {
      console.error('Error fetching applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load organization profile in parallel with other data
  const loadOrganizationProfile = useCallback(async () => {
    if (!currentUser) return null;
    
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', currentUser.uid));
      if (!orgDoc.exists()) {
        throw new Error('Organization profile not found');
      }
      return orgDoc.data();
    } catch (error) {
      console.error('Error loading organization profile:', error);
      throw error;
    }
  }, [currentUser]);
  
  // Initialize dashboard with proper error handling
  useEffect(() => {
    let isMounted = true;
    
    const initializeDashboard = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      setUser(currentUser);
      
      try {
        // Load organization profile first
        const orgData = await loadOrganizationProfile();
        if (!isMounted) return;
        
        setOrgProfile(orgData);
        
        // Then load initial tab data
        if (activeTab === 'jobs') {
          await fetchJobs();
        } else if (activeTab === 'candidates') {
          await fetchCandidates();
        } else if (activeTab === 'applications' && selectedJob) {
          await fetchApplications(selectedJob.id);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        if (isMounted) {
          setError(error.message || 'Failed to load dashboard');
          toast.error(error.message || 'Failed to load dashboard');
          
          if (error.message.includes('organization profile')) {
            navigate('/organization-profile');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    initializeDashboard();
    
    return () => {
      isMounted = false;
    };
  }, [currentUser, navigate, activeTab, selectedJob]);
  
  // Clean up any pending effects when the component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function if needed
    };
  }, []);
  
  // Render the appropriate content based on the active tab
  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'candidates':
        return (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-2xl font-bold text-gray-900">Find Talent</h2>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1 max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, skills, or role..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FaFilter className="mr-2 h-4 w-4" />
                    Filter
                    {selectedSkills.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium">
                        {selectedSkills.length}
                      </span>
                    )}
                  </button>
                  <AnimatePresence>
                    {isFilterOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10 p-4"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-sm font-medium text-gray-700">Filters</h3>
                          <button
                            onClick={clearFilters}
                            className="text-sm text-indigo-600 hover:text-indigo-800"
                          >
                            Clear all
                          </button>
                        </div>
                        <SkillFilter
                          skills={allSkills}
                          selectedSkills={selectedSkills}
                          onSkillToggle={toggleSkill}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            
            {filteredCandidates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCandidates.map((candidate) => (
                  <CandidateCard key={candidate.id} candidate={candidate} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <FaUserTie className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">No candidates found</h3>
                <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        );
      
      case 'jobs':
        return (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">My Job Postings</h2>
              <button
                onClick={() => setActiveTab('post')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FaPlus className="mr-2 h-4 w-4" />
                Post New Job
              </button>
            </div>
            <Suspense fallback={<Spinner size="lg" />}>
              <ManageMyJobs 
                key={refreshJobsKey}
                onJobSelect={handleJobSelect}
                jobs={jobs}
                orgId={user?.uid}
                orgProfile={orgProfile}
              />
            </Suspense>
          </div>
        );
      
      case 'applications':
        return selectedJob ? (
          <div>
            <div className="flex items-center mb-6">
              <button
                onClick={() => setActiveTab('jobs')}
                className="mr-4 text-indigo-600 hover:text-indigo-800"
              >
                <FaChevronLeft className="inline mr-1" /> Back to Jobs
              </button>
              <h2 className="text-2xl font-bold text-gray-900">
                Applications for {selectedJob.title}
              </h2>
            </div>
            <Suspense fallback={<Spinner size="lg" />}>
              <JobApplications 
                jobId={selectedJob.id}
                onBack={() => setSelectedJob(null)}
              />
            </Suspense>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FaBriefcase className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No job selected</h3>
            <p className="mt-1 text-sm text-gray-500">Please select a job to view applications</p>
          </div>
        );
        
      case 'post':
        return (
          <div className="space-y-6">
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab('jobs')}
                className="mr-4 text-indigo-600 hover:text-indigo-800"
              >
                <FaChevronLeft className="inline mr-1" /> Back to Jobs
              </button>
              <h2 className="text-2xl font-bold text-gray-900">Post a New Job</h2>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <JobPostForm 
                orgId={user?.uid} 
                orgName={orgProfile?.name || 'Your Organization'}
                onSuccess={() => {
                  setActiveTab('jobs');
                  setRefreshJobsKey(prev => prev + 1);
                  toast.success('Job posted successfully!');
                }}
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  }, [activeTab, selectedJob, searchTerm, selectedSkills, isFilterOpen, allSkills, filteredCandidates, jobs, refreshJobsKey, user?.uid, orgProfile, clearFilters, toggleSkill]);

  // Main component render
  if (loading && !isInitialized) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <ToastContainer position="top-right" autoClose={5000} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 mb-6">
            <Spinner size="lg" className="w-full h-full text-indigo-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Loading Your Dashboard</h2>
          <p className="text-lg text-gray-600 mb-6">
            We're getting everything ready for you. This should only take a moment...
          </p>
          
          <div className="w-full max-w-md bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-200">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  {user ? (
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <div className="w-3 h-3 rounded-full bg-gray-300 animate-pulse"></div>
                  )}
                </div>
                <span className="text-gray-700">
                  {user ? 'Signed in successfully' : 'Checking authentication...'}
                </span>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  {orgProfile ? (
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <div className="w-3 h-3 rounded-full bg-gray-300 animate-pulse"></div>
                  )}
                </div>
                <span className="text-gray-700">
                  {orgProfile 
                    ? `Loaded profile for ${orgProfile.organizationName || 'your organization'}` 
                    : 'Loading organization profile...'}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  {candidates.length > 0 || jobs.length > 0 ? (
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <div className="w-3 h-3 rounded-full bg-gray-300 animate-pulse"></div>
                  )}
                </div>
                <span className="text-gray-700">
                  {activeTab === 'candidates' && candidates.length > 0 
                    ? `Loaded ${candidates.length} candidates`
                    : activeTab === 'jobs' && jobs.length > 0
                      ? `Loaded ${jobs.length} jobs`
                      : `Loading ${activeTab} data...`}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          <p className="mt-8 text-sm text-gray-500">
            Having trouble? Try refreshing the page or contact support if the issue persists.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  // Initialize post tab as loaded in the initial state
  // No need for a separate effect for this

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <ToastContainer position="top-right" autoClose={5000} />
      <main className="flex-1 container mx-auto px-4 py-6">
        {error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaExclamationTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                >
                  Refresh page <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        ) : !orgProfile ? (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Profile Setup Required</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Please complete your organization profile to access the dashboard.
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <button
                onClick={() => navigate('/app/profile/organization')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Complete Profile Setup
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                    disabled={loadingStates[tab.id]}
                  >
                    {tab.icon}
                    {tab.name}
                    {loadingStates[tab.id] && (
                      <span className="ml-2">
                        <Spinner size="sm" />
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="py-4">
              {renderTabContent()}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default OrganizationDashboard;
